import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import store from '@/stores/store';
import products from './submodules/products';

function movementProductRecord(state) {
    if (state.product?.id === state.product_id) {
        return state.product;
    }
    if (
        state.provider_catalog_product_id
        && state.stock_owner === 'provider'
        && state.product_id === state.provider_catalog_product_id
    ) {
        return state.provider_catalog_product;
    }
    if (
        state.provider_catalog_product?.child_product === state.product_id
        && state.provider_catalog_product?.child_product_data
    ) {
        return state.provider_catalog_product.child_product_data;
    }
    if (state.provider_catalog_product_id && state.stock_owner === 'owner') {
        // Sale product not created yet (done on save): fall back to the supplier product.
        return state.product || state.provider_catalog_product;
    }
    return state.product;
}

function canSetMovementDate(state) {
    const product = movementProductRecord(state);
    const stockRow = state.stocks.find((s) => s.id === state.stock);
    return (
        product?.owner === store.getters['session/current_entity_id']
        && stockRow?.owned === true
    );
}

const state = {
    product: null,
    product_id: null,
    provider_catalog_product_id: null,
    provider_catalog_product: null,
    stock_owner: null,
    stock: null,
    stocks: [],
    stocks_loading: false,
    stocks_loading_errors: {},
    storage_location: null,
    stock_on_hand: null,
    stock_on_hand_loading: false,
    quantity: null,
    quantity_unit: null,
    comment: null,
    adjustment_reason: null,
    movement_date: null,
    last_movement_date: null,
    last_movement_date_loading: false,
    inventory_customer_id: null,
    inventory_owner_id: null,
    inventory_customer_loading: false,
    connected_customers: [],
    movement: {},
    movement_saving: false,
    movement_saving_errors: {},
    movement_quantity_unit_options_loading: false,
};


const mutations = {
    updateProductId(state, product_id) {
        state.product_id = product_id;
        state.product = state.products.products.find(p => p.id === product_id);
    },
    updateProduct(state, product) {
        state.product = product;
        state.product_id = product?.id;
    },
    updateProviderCatalogProduct(state, product) {
        state.provider_catalog_product = product;
        state.provider_catalog_product_id = product?.id ?? null;
    },
    updateStockOwner(state, stock_owner) {
        state.stock_owner = stock_owner;
    },
    updateInventoryCustomerId(state, inventory_customer_id) {
        state.inventory_customer_id = inventory_customer_id;
    },
    updateInventoryOwnerId(state, inventory_owner_id) {
        state.inventory_owner_id = inventory_owner_id;
    },
    updateInventoryCustomerLoading(state, loading) {
        state.inventory_customer_loading = loading;
    },
    updateConnectedCustomers(state, customers) {
        state.connected_customers = customers || [];
    },

    updateMovement(state, movement) {
        state.movement = movement || {};
        state.storage_location = movement?.storage_location || null;
        state.comment = movement?.comment;
        state.product_id = movement?.product;
        state.quantity = movement?.quantity;
        state.quantity_unit = null;
        state.adjustment_reason = movement?.adjustment_reason;
        if (movement && Object.prototype.hasOwnProperty.call(movement, "stock")) {
            state.stock = movement.stock;
        } else {
            state.stock = null;
        }
    },
    updateComment(state, comment) {
        state.comment = comment;
    },
    updateAdjustmentReason(state, adjustment_reason) {
        state.adjustment_reason = adjustment_reason;
    },
    updateQuantity(state, quantity) {
        state.quantity = quantity;
    },
    updateQuantityUnit(state, quantity_unit) {
        state.quantity_unit = quantity_unit;
    },
    updateStorageLocation(state, id) { state.storage_location = id; },
    updateStock(state, stock) {
        if (state.stock !== stock) state.storage_location = null;
        state.stock = stock;
    },
    updateStocksLoading(state, loading) {
        state.stocks_loading = loading;
        state.stocks_loading_errors = {};
    },
    updateStocksLoadingErrors(state, errors) {
        state.stocks_loading = false;
        state.stocks_loading_errors = errors;
    },
    updateStocks(state, stocks) {
        state.stocks = stocks;
        const stocksWithStockTracking = stocks.filter(s => s.track_stock !== false);
        // Preselect only when a single warehouse is available; otherwise force an explicit choice.
        if (!state.stock && stocksWithStockTracking.length === 1) {
            state.stock = stocksWithStockTracking[0].id;
        }
    },
    updateStockOnHand(state, value) {
        state.stock_on_hand = value;
    },
    updateStockOnHandLoading(state, loading) {
        state.stock_on_hand_loading = loading;
    },
    updateMovementDate(state, movement_date) {
        state.movement_date = movement_date;
    },
    updateLastMovementDate(state, last_movement_date) {
        state.last_movement_date = last_movement_date;
    },
    updateLastMovementDateLoading(state, loading) {
        state.last_movement_date_loading = loading;
    },
    updateMovementSaving(state, saving) {
        state.movement_saving = saving;
        state.movement_saving_errors = {};
    },
    updateMovementSavingErrors(state, errors) {
        state.movement_saving = false;
        state.movement_saving_errors = errors;
    },
    updateMovementQuantityUnitOptionsLoading(state, loading) {
        state.movement_quantity_unit_options_loading = loading;
    },
};

const actions = {

    async fetchConnectedCustomers({ commit, rootGetters }) {
        if (rootGetters['session/current_entity_profile'] !== 'operator') {
            commit('updateConnectedCustomers', []);
            return [];
        }
        commit('updateInventoryCustomerLoading', true);
        try {
            const response = await axios.get('/api/customers/', {
                params: {
                    connected: true,
                    limit: 1000,
                    ordering: 'name',
                },
            });
            const customers = response.data?.results || [];
            commit('updateConnectedCustomers', customers);
            return customers;
        } catch (xhr_error) {
            commit('updateConnectedCustomers', []);
            throw utils.handleError(xhr_error);
        } finally {
            commit('updateInventoryCustomerLoading', false);
        }
    },

    async setInventoryCustomer({ commit, dispatch }, customer) {
        commit('updateInventoryCustomerId', customer?.id ?? null);
        commit('updateInventoryOwnerId', customer?.entity ?? null);
        await dispatch('fetchStockOnHand');
        await dispatch('refreshMovementDateConstraints');
    },

    async resolveInventoryCustomer({ commit, state, rootGetters, dispatch }) {
        const isOperator = rootGetters['session/current_entity_profile'] === 'operator';
        if (!isOperator || !state.stock) {
            commit('updateInventoryCustomerId', null);
            if (!isOperator) {
                commit('updateInventoryOwnerId', null);
            }
            return null;
        }
        const currentEntityId = rootGetters['session/current_entity_id'];
        const explicitOwnerId =
            state.movement?.owner
            ?? state.product?.stockproduct_data?.owner
            ?? null;
        const productOwnerId = explicitOwnerId ?? state.product?.owner;
        if (!productOwnerId) {
            commit('updateInventoryCustomerId', null);
            commit('updateInventoryOwnerId', null);
            return null;
        }
        if (Number(productOwnerId) === Number(currentEntityId)) {
            commit('updateInventoryCustomerId', null);
            commit('updateInventoryOwnerId', currentEntityId);
            return null;
        }
        if (!state.connected_customers.length) {
            await dispatch('fetchConnectedCustomers');
        }
        const customer = state.connected_customers.find(
            (row) => Number(row.entity) === Number(productOwnerId),
        );
        if (customer?.id) {
            commit('updateInventoryCustomerId', customer.id);
            commit('updateInventoryOwnerId', customer.entity);
            return customer;
        }
        // Line owner is not a connected customer (e.g. supplier-owned stock).
        commit('updateInventoryCustomerId', null);
        commit('updateInventoryOwnerId', productOwnerId);
        return null;
    },

    async fetchStocks({ commit, dispatch, state }, params) {
        commit('updateStocksLoading', true);
        try {
            const response = await axios.get('/api/stocks/', {
                params: { limit: 1000, owned: true },
            });
            commit('updateStocks', response.data.results);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateStocksLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateStocksLoading', false);
        }
    },

    async ensureSaleProduct({ commit, state }) {
        const childId = state.provider_catalog_product?.child_product;
        const childData = state.provider_catalog_product?.child_product_data;
        if (childId && childData && childData.archived !== true) {
            commit('updateProductId', childId);
            commit('updateProduct', childData);
            return childId;
        }
        const response = await axios.post('/api/importproducts/', {
            products: [state.provider_catalog_product_id],
        });
        const created = response.data?.[0];
        if (!created?.id) {
            throw new Error('Failed to create sale product');
        }
        commit('updateProviderCatalogProduct', {
            ...state.provider_catalog_product,
            child_product: created.id,
            child_product_data: created,
        });
        commit('updateProduct', created);
        return created.id;
    },

    async applyStockOwner({ commit, dispatch, state }, stock_owner) {
        commit('updateStockOwner', stock_owner);
        commit('updateProduct', null);
        commit('updateProductId', null);
        commit('updateStockOnHand', null);
        commit('updateMovementDate', null);
        commit('updateLastMovementDate', null);

        if (!state.provider_catalog_product_id || !stock_owner) {
            return;
        }

        if (stock_owner === 'provider') {
            commit('updateProductId', state.provider_catalog_product_id);
            commit('updateProduct', state.provider_catalog_product);
            await dispatch('fetchStockOnHand');
            await dispatch('refreshMovementDateConstraints');
            return;
        }

        if (stock_owner === 'owner') {
            const childId = state.provider_catalog_product?.child_product;
            if (childId) {
                commit('updateProductId', childId);
                if (state.provider_catalog_product?.child_product_data) {
                    commit(
                        'updateProduct',
                        state.provider_catalog_product.child_product_data,
                    );
                    await dispatch('fetchStockOnHand');
                    await dispatch('refreshMovementDateConstraints');
                } else {
                    await dispatch('fetchProduct');
                }
            } else {
                await dispatch('fetchStockOnHand');
            }
        }
    },

    async refreshMovementDateConstraints({ commit, dispatch, state }) {
        if (!canSetMovementDate(state)) {
            commit('updateMovementDate', null);
            return;
        }
        await dispatch('fetchLastMovementDate');
    },

    async saveMovement({ commit, dispatch, state }, params) {
        commit('updateMovementSaving', true);

        let productId = state.product_id;
        if (state.provider_catalog_product_id) {
            if (state.stock_owner === 'provider') {
                productId = state.provider_catalog_product_id;
            } else if (state.stock_owner === 'owner') {
                productId = await dispatch('ensureSaleProduct');
            }
        }

        const payload = {
            comment: state.comment,
            stock: state.stock,
            storage_location: state.storage_location,
            product: productId,
            quantity: state.quantity,
            movement_type: "adjustment",
        };
        const quantityProduct = movementProductRecord(state);
        if (
            state.quantity_unit
            && quantityProduct
            && state.quantity_unit !== quantityProduct.stock_unit
        ) {
            payload.quantity_unit = state.quantity_unit;
        }
        if (state.adjustment_reason) {
            payload.adjustment_reason = state.adjustment_reason;
        }

        if (canSetMovementDate(state) && state.movement_date) {
            payload.movement_date = state.movement_date;
        }

        if (state.inventory_owner_id) {
            payload.owner = state.inventory_owner_id;
        }

        let url = '/api/movements/';
        let method = axios.post;
        if (state.movement?.id) {
            url = `/api/movements/${state.movement.id}/`;
            method = axios.put;
        }
        
        try {
            const response = await method(url, payload);
            commit('updateMovementSaving', false);
            dispatch("session/fetchStats", null, { root: true });
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateMovementSavingErrors', error.details);
            throw error;
        }
    },

    async fetchMovementQuantityUnitOptions({ commit, state }) {
        if (!state.product_id || !state.product) {
            return [];
        }
        commit('updateMovementQuantityUnitOptionsLoading', true);
        try {
            const response = await axios.get('/api/formats/', {
                params: {
                    limit: 10000,
                },
            });
            const options = $utils.buildMovementQuantityUnitOptions(
                state.product,
                response.data?.results || [],
            );
            commit('updateProduct', {
                ...state.product,
                movement_quantity_unit_options: options.map((option) => ({
                    value: option.value,
                    label: option.text,
                    format: option.format,
                })),
            });
            return options;
        } catch (xhr_error) {
            return $utils.getComponentUnitOptions(state.product);
        } finally {
            commit('updateMovementQuantityUnitOptionsLoading', false);
        }
    },

    async fetchProduct({ commit, dispatch, state }, { force = false } = {}) {
        if (!state.product_id) {
            return null;
        }

        const hasOptions = (state.product?.movement_quantity_unit_options || []).length > 0;
        if (!force && state.product?.id === state.product_id && hasOptions) {
            await dispatch('fetchStockOnHand');
            return state.product;
        }

        try {
            const response = await axios.get(`/api/products/${state.product_id}/`);
            commit('updateProduct', response.data);
            await dispatch('fetchMovementQuantityUnitOptions');
            await dispatch('resolveInventoryCustomer');
            await dispatch('fetchStockOnHand');
            await dispatch('refreshMovementDateConstraints');
            return state.product;
        } catch (xhr_error) {
            if (state.product?.id === state.product_id) {
                await dispatch('resolveInventoryCustomer');
                await dispatch('fetchStockOnHand');
                await dispatch('refreshMovementDateConstraints');
                return state.product;
            }
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async fetchLastMovementDate({ commit, state }) {
        if (!state.product_id || !state.stock) {
            commit('updateLastMovementDate', null);
            commit('updateLastMovementDateLoading', false);
            return;
        }
        commit('updateLastMovementDateLoading', true);
        try {
            const response = await axios.get('/api/movements/', {
                params: {
                    product: state.product_id,
                    stock: state.stock,
                    owner: state.inventory_owner_id || undefined,
                    ordering: '-movement_date',
                    limit: 1,
                },
            });
            const row = response.data?.results?.[0];
            commit('updateLastMovementDate', row?.movement_date ?? null);
        } catch (xhr_error) {
            commit('updateLastMovementDate', null);
        } finally {
            commit('updateLastMovementDateLoading', false);
        }
    },

    async fetchStockOnHand({ commit, state }) {
        if (!state.product_id || !state.stock) {
            const saleProductPending =
                state.stock
                && state.provider_catalog_product_id
                && state.stock_owner === 'owner'
                && !state.provider_catalog_product?.child_product;
            // The sale product does not exist yet, so its stock is necessarily empty.
            commit('updateStockOnHand', saleProductPending ? 0 : null);
            commit('updateStockOnHandLoading', false);
            return;
        }
        commit('updateStockOnHandLoading', true);
        try {
            const params = {
                product: state.product_id,
                stock: state.stock,
                limit: 1,
            };
            if (state.inventory_owner_id) {
                params.owner = state.inventory_owner_id;
            }
            const response = await axios.get('/api/stockproducts/', {
                params,
            });
            const row = response.data?.results?.[0];
            commit('updateStockOnHand', state.storage_location ? (row?.location_balances?.find(balance => balance.storage_location === state.storage_location)?.count ?? 0) : (row != null ? row.count : 0));
        } catch (xhr_error) {
            commit('updateStockOnHand', null);
        } finally {
            commit('updateStockOnHandLoading', false);
        }
    },

    async init({ commit, dispatch, state, rootGetters }, params) {
        commit('updateMovement', params.movement);
        commit('updateProduct', null);
        commit('updateProviderCatalogProduct', null);
        commit('updateStockOwner', null);
        commit('updateInventoryCustomerId', null);
        commit('updateInventoryOwnerId', null);
        commit('updateConnectedCustomers', []);
        commit('updateStockOnHand', null);
        commit('updateMovementDate', null);
        commit('updateLastMovementDate', null);
        dispatch('fetchStocks');
        await dispatch('products/init');
        if (rootGetters['session/current_entity_profile'] === 'operator') {
            await dispatch('fetchConnectedCustomers');
        }

        const catalogProduct =
            params?.movement?.provider_catalog_product_data
            || (params?.movement?.provider_catalog_product
                ? { id: params.movement.provider_catalog_product }
                : null);

        if (catalogProduct?.id) {
            commit('updateProviderCatalogProduct', catalogProduct);
            return;
        }

        if (!params?.movement?.product) {
            dispatch('products/fetchProducts');
        } else {
            commit('updateProductId', params.movement.product);
            if (params.movement.product_data) {
                commit('updateProduct', params.movement.product_data);
            }
            await dispatch('fetchProduct', { force: true });
        }
    }
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    modules: {
        products: products(),
    },
};
